import genComponentDesc from './genComponentDesc'
import { warnDeprecated } from '../utils/deprecate'
import hyphenateToCamel from '../utils/hyphenateToCamel'

// Get config from slot
export function getConfigFromSlot(vnode) {
  const config = {}

  if (vnode.tag) {
    const name = hyphenateToCamel(vnode.tag)

    if (name === 'layout') {
      config.type = 'layout'
    } else {
      config.name = name
    }

    if (vnode.data) {
      const { attrs } = vnode.data
      const {
        show, label, required, validation, node, type, layout, component,
      } = attrs
      const requiredMessage = attrs.requiredMessage ?? attrs['required-message']
      const defaultValue = attrs.defaultValue ?? attrs['default-value']
      const formItem = attrs.formItem ?? attrs['form-item']

      if (label !== undefined) {
        config.label = label
      }

      if (defaultValue !== undefined) {
        config.defaultValue = defaultValue
      }

      if (show !== undefined) {
        config.show = show
      }

      if (required !== undefined) {
        config.required = required !== false
      } else {
        config.required = false
      }

      if (typeof requiredMessage === 'string') {
        config.requiredMessage = requiredMessage
      }

      if (validation !== undefined) {
        config.validation = validation
      }

      if (formItem || node) {
        config.formItem = formItem ?? node
      }

      if (type) {
        config.type = config.type ?? type
      }

      if (layout) {
        config.layout = layout
      }

      if (component) {
        config.component = component
      }
    }
  } else if (!vnode.data && !vnode.children) {
    return false
  }

  if (vnode.children) {
    vnode.children.forEach(child => {
      config.children = config.children || []

      if (getConfigFromSlot(child)) {
        config.children.push(getConfigFromSlot(child))
      }
    })
  }

  return config
}

function stdDefaultValue(config) {
  if (config.defaultValue !== undefined) {
    return config.defaultValue
  }

  if (config.node?.default !== undefined) {
    warnDeprecated('node.default', 'default')

    return config.node.default
  }

  return undefined
}

function stdShow(config) {
  let show = true

  if (typeof config.show === 'boolean' || typeof config.show === 'function') {
    show = config.show
  } else if (config.node?.show) {
    show = config.node.show ?? true

    delete config.node.show
  }

  return show
}

function stdRequired(config) {
  if (config.node?.required !== undefined) {
    warnDeprecated('node.required', 'required')

    const { required } = config.node

    delete config.node.required

    return required
  }

  return config.required ?? false
}

function stdValidation(config) {
  if (config.node?.validation !== undefined) {
    const { validation } = config.node

    warnDeprecated('node.validation', 'validation')

    delete config.node.validation

    return validation
  }

  return config.validation
}

function stdLayout(config) {
  if (config.layout?.type) {
    warnDeprecated('layout.type', 'layout.is')

    config.layout.is = config.layout.type

    delete config.layout.type
  }

  const layout = genComponentDesc(config.layout, 'block')

  if (typeof config.label === 'string') {
    layout.props.label = config.label
  }

  return layout
}

function stdFormItem(config) {
  let { formItem, node } = config

  if (node) {
    const { validation, ...formItemProps } = config.node // eslint-disable-line @typescript-eslint/no-unused-vars
    formItem = formItemProps

    delete config.node

    warnDeprecated('node', 'formItem')
  }

  const desc = genComponentDesc(formItem, 'formItem')

  if (typeof config.label === 'string') {
    desc.props.label = config.label
  }

  return desc
}

// Standard strcut like
/**
 *  {
 *    name: string
 *    defaultValue: any
 *    show: boolean | funciton // default true
 *    required: boolean
 *    validation: () => Promise.resolve()
 *    formItem: {
 *      is: string | Component
 *      props: object
 *      on: object
 *    }
 *
 *    children: array
 *    type: string
 *    layout: {
 *      is: string | Component
 *      props: object
 *      on: object
 *    }
 *
 *    component: {
 *      is: string | Component
 *      props: object
 *      on: object
 *    }
 *  }
 */
export function stdConfig(config, readonly) {
  const cnf = {}

  if (config.name) {
    cnf.name = config.name
  }

  cnf.show = stdShow(config)
  cnf.required = stdRequired(config)

  if (config.requiredMessage !== undefined) {
    cnf.requiredMessage = config.requiredMessage
  }

  cnf.validation = stdValidation(config)

  if (config.children) {
    cnf.children = config.children.map(child => stdConfig(child, readonly))

    cnf.type = config.type ?? 'object'

    cnf.layout = stdLayout(config)
  } else {
    let defaultCom = 'input'

    if (config.type) {
      cnf.type = config.type

      if (config.type === 'boolean' && (!config.component || !config.component.is)) {
        defaultCom = 'Checkbox'
      }
    }

    const defaultValue = stdDefaultValue(config)

    if (defaultValue !== undefined) {
      cnf.defaultValue = defaultValue
    }

    cnf.formItem = stdFormItem(config)

    const component = genComponentDesc(config.component, defaultCom)
    if (typeof readonly === 'boolean') {
      component.props.readonly = readonly ?? false
    }
    cnf.component = component
  }

  return cnf
}
